import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { VideoCourseCard } from '@/components/VideoCourseCard';
import { Video, Film, GraduationCap, Clapperboard, FileVideo, Plus, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';
import { adminFetch } from '@/utils/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'all', label: 'All Courses', icon: Film },
  { id: 'instructional', label: 'Instructional', icon: GraduationCap },
  { id: 'performance', label: 'Performance', icon: Clapperboard },
  { id: 'documentary', label: 'Documentary', icon: FileVideo },
];

export const Videos = () => {
  const hero = useHero('videos');
  const { enabled } = usePageEditMode();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPrice, setNewPrice] = useState('19.99');
  const [newCategory, setNewCategory] = useState('instructional');
  const [newThumb, setNewThumb] = useState('');
  const [creating, setCreating] = useState(false);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API}/video-courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error loading video courses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCourses(); }, []);

  const handleCreate = async () => {
    const title = newTitle.trim();
    if (!title) { toast.error('Enter a title'); return; }
    if (!newThumb.trim()) { toast.error('Enter a thumbnail URL'); return; }
    setCreating(true);
    try {
      const body = {
        title,
        description: '',
        instructor: 'Mestre Curtis Pierre',
        category: newCategory,
        price: parseFloat(newPrice) || 0,
        thumbnail_url: newThumb.trim(),
        video_ids: []
      };
      const res = await adminFetch('/admin/video-courses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Create failed');
      toast.success('Video course created');
      setShowAddModal(false);
      setNewTitle(''); setNewPrice('19.99'); setNewThumb(''); setNewCategory('instructional');
      await loadCourses();
    } catch (e) {
      toast.error(e.message || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (course) => {
    if (!window.confirm(`Delete "${course.title}"? This cannot be undone.`)) return;
    try {
      const res = await adminFetch(`/admin/video-courses/${course.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Video course deleted');
      await loadCourses();
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const filteredCourses = selectedCategory === 'all' 
    ? courses 
    : courses.filter(course => course.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="loading-spinner">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-16"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary font-medium mb-6">
          <Video className="w-4 h-4" />
          <PageEditable path="hero.eyebrow" fallback={hero.kicker || "VIDEO COURSES"} />
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-foreground mb-6">
          <PageEditable path="hero.title" fallback={hero.title_top || "Learn from the"} />
          <span className="block text-primary mt-2"><PageEditable path="hero.titleAccent" fallback={hero.title_bottom || "Master"} /></span>
        </h1>
        <PageEditable
          path="hero.subtitle"
          as="p"
          multiline
          className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto"
          fallback="Exclusive video courses on Capoeira, Brasilian percussion, and Afro-Brasilian culture taught by Mestre Curtis Pierre with over 38 years of experience."
        />
      </motion.div>

      {/* Category Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-wrap justify-center gap-3 mb-12"
      >
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const isActive = selectedCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border text-foreground hover:border-primary/50'
              }`}
              data-testid={`category-${cat.id}`}
            >
              <Icon className="w-4 h-4" />
              <PageEditable path={`categoryLabels.${cat.id}`} as="span" fallback={cat.label} />
            </button>
          );
        })}
      </motion.div>

      {/* Courses Grid */}
      {filteredCourses.length === 0 && !enabled ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-20"
        >
          <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-bold text-foreground mb-2">
            {selectedCategory === 'all'
              ? <PageEditable path="empty.titleAll" as="span" fallback="No courses available yet" />
              : <PageEditable path="empty.titleFiltered" as="span" fallback="No courses in this category" />}
          </h3>
          <p className="text-muted-foreground">
            <PageEditable path="empty.body" as="span" multiline fallback="Check back soon for new video content!" />
          </p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCourses.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <VideoCourseCard course={course} onDelete={handleDelete} />
            </motion.div>
          ))}
          {enabled && (
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              data-edit-safe
              data-testid="add-video-course-btn"
              className="min-h-[420px] flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 hover:border-primary transition-all text-primary font-bold"
            >
              <Plus className="w-12 h-12" />
              <span className="text-lg">Add New Video Course</span>
              <span className="text-xs text-muted-foreground font-normal">Creates an empty video course</span>
            </button>
          )}
        </div>
      )}

      {/* Features Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-20 grid md:grid-cols-3 gap-8"
      >
        <div className="text-center p-8 bg-card border border-border/50 rounded-2xl">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <Video className="w-7 h-7 text-primary" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">
            <PageEditable path="feature.stream.title" fallback="Stream Anywhere" />
          </h3>
          <PageEditable
            path="feature.stream.body"
            as="p"
            multiline
            className="text-muted-foreground text-sm"
            fallback="Watch on any device - computer, tablet, or phone. Your courses, always accessible."
          />
        </div>
        <div className="text-center p-8 bg-card border border-border/50 rounded-2xl">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <GraduationCap className="w-7 h-7 text-primary" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">
            <PageEditable path="feature.download.title" fallback="Download & Keep" />
          </h3>
          <PageEditable
            path="feature.download.body"
            as="p"
            multiline
            className="text-muted-foreground text-sm"
            fallback="Download videos to watch offline. Once purchased, they're yours forever."
          />
        </div>
        <div className="text-center p-8 bg-card border border-border/50 rounded-2xl">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <Film className="w-7 h-7 text-primary" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">
            <PageEditable path="feature.hd.title" fallback="HD Quality" />
          </h3>
          <PageEditable
            path="feature.hd.body"
            as="p"
            multiline
            className="text-muted-foreground text-sm"
            fallback="Crystal clear video and audio quality for the best learning experience."
          />
        </div>
      </motion.div>

      {/* Add Video Course Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70"
          onClick={() => !creating && setShowAddModal(false)}
          data-edit-safe
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-edit-safe
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Add New Video Course</h3>
              <button
                type="button"
                onClick={() => !creating && setShowAddModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Title *</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Berimbau Fundamentals"
                  data-testid="new-video-title-input"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  data-testid="new-video-category-select"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="instructional">Instructional</option>
                  <option value="performance">Performance</option>
                  <option value="documentary">Documentary</option>
                  <option value="general">General</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Price (USD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  data-testid="new-video-price-input"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Thumbnail URL *</label>
                <input
                  type="text"
                  value={newThumb}
                  onChange={(e) => setNewThumb(e.target.value)}
                  placeholder="https://…"
                  data-testid="new-video-thumb-input"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                A blank video course will be created. Add videos and edit fields inline afterwards.
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => !creating && setShowAddModal(false)}
                className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating || !newTitle.trim() || !newThumb.trim()}
                data-testid="create-video-btn"
                className="flex-1 py-3 bg-primary hover:bg-primary/90 text-black rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

const VideosWithEdit = () => (
  <PageEditProvider slug="videos">
    <Videos />
    <PageEditToolbar />
  </PageEditProvider>
);

export default VideosWithEdit;
